import { useState, useEffect } from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Check, Copy } from 'lucide-react'
import { useProcessorById } from '@/lib/api/processors'
import { useUpdateProcessor } from '@/lib/api/processors'
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id'
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { config } from '@/lib/config'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { SecretInput } from '@/components/form/secret-input'
import { ProcessorEditHeader } from '../../components/processor-edit-header'
import { AirwallexEditSkeleton } from '../components/airwallex-edit-skeleton'
import { MultiSelect } from '@/components/ui/multi-select'
import { ProcessorApmSettings, ApmSettings, initApmSettingsFromOptions } from '../../components/processor-apm-settings'
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field'
import { minorUnitsToMajorUnits, moneyStringOrNumberToMinorUnits } from '@/lib/money'

const CURRENCY_OPTIONS = [
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'AUD', label: 'AUD - Australian Dollar' },
    { value: 'CAD', label: 'CAD - Canadian Dollar' },
    { value: 'JPY', label: 'JPY - Japanese Yen' },
    { value: 'CNY', label: 'CNY - Chinese Yuan' },
    { value: 'CHF', label: 'CHF - Swiss Franc' },
    { value: 'NZD', label: 'NZD - New Zealand Dollar' },
    { value: 'SGD', label: 'SGD - Singapore Dollar' },
    { value: 'HKD', label: 'HKD - Hong Kong Dollar' },
    { value: 'SEK', label: 'SEK - Swedish Krona' },
    { value: 'NOK', label: 'NOK - Norwegian Krone' },
    { value: 'DKK', label: 'DKK - Danish Krone' },
]

const AirwallexEditPageInner = () => {
    const id = useProcessorEditId()
    const navigate = useNavigate()
    const { t } = useTranslation('processors')
    const { toast } = useToast()
    const processorExit = useProcessorEditExit()

    const { data: processor, isLoading } = useProcessorById(id)
    const updateProcessorMutation = useUpdateProcessor()

    const [validationError, setValidationError] = useState<string | null>(null)
    const [copiedWebhook, setCopiedWebhook] = useState(false)
    const webhookUrl = `${config.apiBaseUrl}/api/public/webhooks/airwallex`
    const [apmSettings, setApmSettings] = useState<ApmSettings>({})
    const [formData, setFormData] = useState({
        name: '',
        enabled: true,
        baseCurrency: 'AUD',
        supportedCurrencies: [] as string[],
        monthlyProcessingCapacity: 0,
        testMode: false,
        clientId: '',
        clientApiKey: '',
        webhookSecret: '',
        descriptor: '',
        dryMode: false,
    })

    useEffect(() => {
        if (processor) {
            const options = processor.options as any
            setFormData({
                name: processor.name,
                enabled: processor.enabled ?? true,
                baseCurrency: processor.baseCurrency || 'AUD',
                supportedCurrencies: processor.supportedCurrencies || [],
                monthlyProcessingCapacity: minorUnitsToMajorUnits(
                    processor.monthlyProcessingCapacity || 0,
                    processor.baseCurrency || 'AUD',
                ),
                testMode: options?.testMode ?? false,
                clientId: options?.clientId || '',
                clientApiKey: options?.clientApiKey || '',
                webhookSecret: options?.webhookSecret || '',
                descriptor: options?.descriptor?.descriptor || '',
                dryMode: options?.dryMode === true,
            })
            setApmSettings(initApmSettingsFromOptions('airwallex', options?.apmSettings as Record<string, Record<string, unknown>> | undefined))
        }
    }, [processor])

    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            setValidationError(t('nameRequired'))
            return
        }

        if (!formData.clientId.trim()) {
            setValidationError(t('clientIdRequired'))
            return
        }

        if (!formData.clientApiKey.trim()) {
            setValidationError(t('clientApiKeyRequired'))
            return
        }

        if (!formData.webhookSecret.trim()) {
            setValidationError(t('webhookSecretRequired'))
            return
        }

        const supportedCurrencies = formData.supportedCurrencies.includes(formData.baseCurrency)
            ? formData.supportedCurrencies
            : [...formData.supportedCurrencies, formData.baseCurrency]

        try {
            await updateProcessorMutation.mutateAsync({
                processorId: processor!.id,
                updatedData: {
                    name: formData.name,
                    type: 'airwallex',
                    enabled: formData.enabled,
                    baseCurrency: formData.baseCurrency,
                    supportedCurrencies,
                    monthlyProcessingCapacity: moneyStringOrNumberToMinorUnits(
                        formData.monthlyProcessingCapacity,
                        formData.baseCurrency,
                    ),
                    options: {
                        testMode: formData.testMode,
                        clientId: formData.clientId,
                        clientApiKey: formData.clientApiKey,
                        webhookSecret: formData.webhookSecret,
                        descriptor: {
                            descriptor: formData.descriptor,
                        },
                        apmSettings,
                        dryMode: formData.dryMode,
                    },
                } as any,
            })

            toast({ description: t('processorUpdated') })
            if (processorExit.isEmbed) {
                processorExit.exitAfterSave()
                return
            }
            navigate(-1)
        } catch (error) {
            console.error('Error saving processor:', error)
            toast({ description: t('failedToSave'), variant: 'destructive' })
        }
    }

    const handleCancel = () => {
        if (processorExit.isEmbed) {
            processorExit.exitCancel()
            return
        }
        navigate(-1)
    }

    const handleCopyWebhook = async () => {
        try {
            await navigator.clipboard.writeText(webhookUrl)
            setCopiedWebhook(true)
            setTimeout(() => setCopiedWebhook(false), 2000)
            toast({ description: 'Webhook URL copied to clipboard' })
        } catch {
            toast({ description: 'Failed to copy webhook URL', variant: 'destructive' })
        }
    }

    if (isLoading) {
        return <AirwallexEditSkeleton />
    }

    if (!processor || processor.type !== 'airwallex') {
        return (
            <div className="container mx-auto py-8">
                <Alert variant="destructive">
                    <AlertDescription>{t('processorNotFound')}</AlertDescription>
                </Alert>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-8">
            <ProcessorEditHeader
                processorId={processor.id}
                title={t('editAirwallexConfiguration')}
                onCancel={handleCancel}
                onSave={handleSubmit}
                isSaving={updateProcessorMutation.isPending}
            />

            {validationError && (
                <Alert variant="destructive" className="mb-6">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{validationError}</AlertDescription>
                </Alert>
            )}

            <Tabs defaultValue="configuration">
                <TabsList className="mb-6">
                    <TabsTrigger value="configuration">{t('airwallex.tabs.configuration')}</TabsTrigger>
                    <TabsTrigger value="payment-methods">{t('airwallex.tabs.paymentMethods')}</TabsTrigger>
                </TabsList>

                <TabsContent value="configuration">
            <div className="space-y-6">

                <Card>
                    <CardHeader>
                        <CardTitle>{t('generalSettings')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="name">{t('processorName')}</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>

                        <div>
                            <Label htmlFor="baseCurrency">{t('baseCurrency')}</Label>
                            <select
                                id="baseCurrency"
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                                value={formData.baseCurrency}
                                onChange={(e) => setFormData({ ...formData, baseCurrency: e.target.value })}
                            >
                                {CURRENCY_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <Label htmlFor="supportedCurrencies">{t('supportedCurrencies')}</Label>
                            <MultiSelect
                                options={CURRENCY_OPTIONS}
                                selected={formData.supportedCurrencies}
                                onChange={(currencies) =>
                                    setFormData({ ...formData, supportedCurrencies: currencies })
                                }
                                placeholder={t('selectCurrencies')}
                            />
                        </div>

                        <div>
                            <Label htmlFor="monthlyProcessingCapacity">
                                {t('monthlyProcessingCapacity')}
                            </Label>
                            <Input
                                id="monthlyProcessingCapacity"
                                type="number"
                                min={0}
                                value={formData.monthlyProcessingCapacity}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        monthlyProcessingCapacity: Math.max(0, Number(e.target.value)),
                                    })
                                }
                            />
                            <p className="text-sm text-muted-foreground mt-1">
                                {t('monthlyProcessingCapacityDescription')}
                            </p>
                        </div>

                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="enabled"
                                checked={formData.enabled}
                                onCheckedChange={(checked) =>
                                    setFormData({ ...formData, enabled: checked === true })
                                }
                            />
                            <Label htmlFor="enabled">{t('enabled')}</Label>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('apiCredentials')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                            <p className="text-sm text-blue-700">
                                <strong>{t('howToGetCredentials')}:</strong>
                            </p>
                            <ol className="mt-2 list-decimal pl-4 text-sm text-blue-700">
                                <li>{t('airwallexLoginToDashboard')}</li>
                                <li>{t('airwallexNavigateToApiSettings')}</li>
                                <li>{t('airwallexCreateOrCopyCredentials')}</li>
                                <li>{t('airwallexUseCorrectEnvironment')}</li>
                            </ol>
                        </div>

                        <div>
                            <Label htmlFor="clientId">{t('clientId')} *</Label>
                            <Input
                                id="clientId"
                                value={formData.clientId}
                                onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                                placeholder={t('clientIdPlaceholder')}
                            />
                        </div>

                        <div>
                            <SecretInput
                                label={`${t('clientApiKey')} *`}
                                value={formData.clientApiKey}
                                onChange={(value) => setFormData({ ...formData, clientApiKey: value })}
                                placeholder={t('clientApiKeyPlaceholder')}
                            />
                        </div>

                        <div>
                            <SecretInput
                                label={`${t('webhookSecret')} *`}
                                value={formData.webhookSecret}
                                onChange={(value) => setFormData({ ...formData, webhookSecret: value })}
                                placeholder={t('webhookSecretPlaceholder')}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>{t('airwallex.webhook.urlLabel')}</Label>
                            <div className="flex items-center gap-2">
                                <Input value={webhookUrl} readOnly className="font-mono text-xs" />
                                <Button type="button" variant="outline" size="icon" onClick={handleCopyWebhook}>
                                    {copiedWebhook ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {t('airwallex.webhook.urlDescription')}
                            </p>
                        </div>

                        <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                            <p className="text-sm text-blue-700">
                                <strong>{t('howToGetWebhookSecret')}:</strong>
                            </p>
                            <ol className="mt-2 list-decimal pl-4 text-sm text-blue-700">
                                <li>{t('airwallexLoginToDashboard')}</li>
                                <li>{t('airwallexNavigateToApiSettings')}</li>
                                <li>{t('airwallexCreateOrCopyWebhookSecret')}</li>
                            </ol>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('testModeSettings')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="testMode"
                                checked={formData.testMode}
                                onCheckedChange={(checked) =>
                                    setFormData({ ...formData, testMode: checked === true })
                                }
                            />
                            <Label htmlFor="testMode">{t('enableTestMode')}</Label>
                        </div>

                        {formData.testMode && (
                            <div className="rounded-lg bg-yellow-50 p-4 border border-yellow-200">
                                <p className="text-sm text-yellow-700">
                                    ⚠️ {t('airwallexTestModeWarning')}
                                </p>
                                <a
                                    href="https://www.airwallex.com/docs/online-payments__testing"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-2 inline-block text-sm text-blue-600 underline hover:text-blue-800"
                                >
                                    {t('viewTestCardNumbers')} →
                                </a>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('statementDescriptor')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="descriptor">{t('descriptor')}</Label>
                            <Input
                                id="descriptor"
                                value={formData.descriptor}
                                onChange={(e) => setFormData({ ...formData, descriptor: e.target.value })}
                                maxLength={32}
                                placeholder={t('descriptorPlaceholder')}
                            />
                            <p className="text-sm text-muted-foreground mt-1">
                                {t('descriptorMaxLength')}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                    disabled={updateProcessorMutation.isPending}
                />
            </div>
                </TabsContent>

                <TabsContent value="payment-methods">
                    <ProcessorApmSettings
                        processorType="airwallex"
                        value={apmSettings}
                        onChange={setApmSettings}
                        disabled={updateProcessorMutation.isPending}
                    />
                </TabsContent>
            </Tabs>
        </div>
    )
}

export const AirwallexEditPage = withRequiredPermission(AirwallexEditPageInner, PERMISSIONS.PROCESSORS_MANAGE)

